import { useT } from "../lib/i18n.tsx";
import { useQuery } from "../lib/hooks.ts";
import { linkProps } from "../lib/router.ts";
import type { PortOverview } from "../lib/types.ts";
import { Apribile, Badge, Empty, ErrorNotice, Spinner } from "../components/ui.tsx";

/**
 * Mappa delle porte host.
 *
 * Risponde alla domanda che altrimenti si risolve a memoria o con `lsof`:
 * quali porte sono occupate, da chi, e quali restano libere.
 */
export function PortsPage() {
  const { data, error, loading } = useQuery<PortOverview>("/ports");
  const t = useT();

  const disponibili =
    data === null ? 0 : data.pool.end - data.pool.start + 1 - data.allocated.length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">{t.t("porte.titolo")}</h1>
        <p className="text-sm tenue">{t.t("porte.intro")}</p>
      </div>

      <ErrorNotice error={error} />
      {loading && <Spinner className="tenue" />}

      {data && (
        <>
          <p className="font-mono text-sm">
            {t.t("porte.riepilogo", {
              da: data.pool.start,
              a: data.pool.end,
              assegnate: t.numero(data.allocated.length),
              libere: t.numero(disponibili),
            })}
          </p>
          <p className="text-sm tenue">
            {t.t("porte.comeChiederla")} <code>{t.t("porte.esempio")}</code>.
          </p>

          {data.allocated.length === 0 ? (
            <Empty title={t.t("porte.nessuna")} hint={t.t("porte.nessunaAiuto")} />
          ) : (
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full text-sm">
                <thead className="superficie text-left text-xs tenue">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t.t("porte.colPorta")}</th>
                    <th className="px-4 py-2 font-medium">{t.t("porte.colStack")}</th>
                    <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("porte.colVariabile")}</th>
                    <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("porte.colServizio")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.allocated.map((a) => (
                    <tr key={`${a.port}/${a.protocol}`}>
                      <td className="px-4 py-2.5 font-mono font-medium">
                        {a.port}
                        <span className="tenue">/{a.protocol}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <a {...linkProps(`/stacks/${a.stackId}`)} className="hover:underline">
                          {a.stackName}
                        </a>
                      </td>
                      <td className="hidden px-4 py-2.5 font-mono text-xs sm:table-cell">{a.variable}</td>
                      <td className="hidden px-4 py-2.5 font-mono text-xs sm:table-cell">
                        {a.service}:{a.containerPort}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Apribile
            titolo={
              <>
                {t.t("porte.esterne")} <Badge>{data.external.length}</Badge>
              </>
            }
          >
            <p className="mb-3 text-sm tenue">{t.t("porte.esterneAiuto")}</p>
            <ul className="space-y-1 text-sm tenue">
              {data.external.map((porta) => (
                <li key={`${porta.port}/${porta.protocol}`} className="flex gap-4">
                  <span className="w-24 font-mono">
                    {porta.port}/{porta.protocol}
                  </span>
                  <span className="truncate">{porta.containerName}</span>
                </li>
              ))}
            </ul>
          </Apribile>
        </>
      )}
    </div>
  );
}
